import React from "react";
import { useLang } from "@/context/LanguageContext";
import { useSeo, useJsonLd } from "@/lib/api";
import { site } from "@/config/site";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import Breadcrumb from "@/components/common/Breadcrumb";
import FinalCta from "@/components/common/FinalCta";

export default function About() {
  const { t, lang } = useLang();
  const a = t.about;
  useSeo({ lang, path: t.nav.about.path, title: `${t.nav.about.label} — NEKASSUR`, description: a.conviction });
  useJsonLd("ld-person", {
    "@context": "https://schema.org",
    "@type": "Person",
    name: site.founder,
    jobTitle: "Fondateur — Courtier en assurances",
    worksFor: { "@type": "InsuranceAgency", name: "NEKASSUR" },
  });

  return (
    <>
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: t.nav.about.label }]} />
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <Eyebrow className="!text-nek-teal-100">{a.eyebrow}</Eyebrow>
              <h1 className="nek-h1 mt-4 text-white" style={{ fontSize: "clamp(2.2rem,4vw,3.4rem)" }}>{a.title}</h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">{a.conviction}</p>
            </Reveal>
            <Reveal className="lg:col-span-5">
              {site.flags.showFounderPhoto && site.media.founderPhotoUrl ? (
                <div className="overflow-hidden rounded-[20px] border border-white/10 shadow-lift">
                  <img src={site.media.founderPhotoUrl} alt="Yanis Nekhla, fondateur de NEKASSUR" className="h-[420px] w-full object-cover" />
                </div>
              ) : (
                <div className="flex h-[340px] items-center justify-center rounded-[20px] border border-white/10 bg-nek-navy-800 sm:h-[420px]">
                  <div className="text-center">
                    <span className="font-heading text-6xl font-extrabold tracking-tight text-nek-teal-100 sm:text-7xl">YN</span>
                    <p className="mt-4 font-heading text-xl font-bold text-white">Yanis Nekhla</p>
                    <p className="mt-1 text-sm text-white/60">{a.founderRole}</p>
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <Section tone="white">
        <Reveal className="max-w-2xl"><h2 className="nek-h2" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)" }}>{a.journeyTitle}</h2></Reveal>
        <ol className="mt-10 space-y-4">
          {a.journey.map((step, i) => (
            <Reveal as="li" key={step.title} className="flex gap-5 rounded-2xl border border-nek-border bg-white p-6">
              <span className="font-heading text-3xl font-extrabold text-nek-blue/30">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="text-lg font-heading font-bold text-nek-navy">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-nek-gray">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </Section>

      <Section tone="light">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-2xl font-heading font-bold text-nek-navy">{a.valuesTitle}</h2>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {a.values.map((v) => (
                <span key={v} className="inline-flex items-center rounded-full border border-nek-border bg-white px-4 py-2 text-sm font-semibold text-nek-navy">
                  {v}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <div className="rounded-2xl bg-nek-navy p-8 text-white">
              <h2 className="text-2xl font-heading font-bold text-white">{a.ambitionTitle}</h2>
              <p className="mt-4 leading-relaxed text-white/75">{a.ambition}</p>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-8 rounded-2xl border border-dashed border-nek-border bg-white p-8">
          <h2 className="text-xl font-heading font-bold text-nek-navy">{a.teamTitle}</h2>
          <p className="mt-2 max-w-2xl text-sm text-nek-gray">{a.teamText}</p>
        </Reveal>
      </Section>

      <FinalCta />
    </>
  );
}
